"use client";

import { useEffect, useState } from "react";
import { createClient } from "@/lib/supabase";
import type {
    Profile,
    Experience,
    Education,
    Skill,
    Certificate,
} from "@/types/database";

// ─── Generic fetch hook ───
function useSupabaseQuery<T>(
    table: string,
    orderBy: string = "sort_order",
    ascending: boolean = true
) {
    const [data, setData] = useState<T[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const supabase = createClient();
        supabase
            .from(table)
            .select("*")
            .order(orderBy, { ascending })
            .then(({ data: rows }) => {
                setData((rows as T[]) ?? []);
                setLoading(false);
            });
    }, [table, orderBy, ascending]);

    return { data, loading };
}

// ─── Profile (single row) ───
export function useProfile() {
    const [profile, setProfile] = useState<Profile | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const supabase = createClient();
        supabase
            .from("profile")
            .select("*")
            .limit(1)
            .single()
            .then(({ data }) => {
                setProfile(data as Profile | null);
                setLoading(false);
            });
    }, []);

    return { profile, loading };
}

// ─── Experiences ───
export function useExperiences() {
    return useSupabaseQuery<Experience>("experiences", "sort_order", true);
}

// ─── Education ───
export function useEducation() {
    return useSupabaseQuery<Education>("education", "sort_order", true);
}

// ─── Skills ───
export function useSkills() {
    return useSupabaseQuery<Skill>("skills", "sort_order", true);
}

// ─── Certificates ───
export function useCertificates() {
    return useSupabaseQuery<Certificate>("certificates", "sort_order", true);
}
